import { useState } from "react";
import { X, Loader2, Upload, ImageIcon, Trash2, UserPlus } from "lucide-react";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Button } from "../ui/Button";
import { influencerService } from "../../services/influencers";
import { toast } from "sonner";

interface AddInfluencerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: any) => void;
}

export function AddInfluencerModal({ isOpen, onClose, onSuccess }: AddInfluencerModalProps) {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    summary: "",
    cover: "",
    active: true,
  });

  if (!isOpen) return null;

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (value: string) => {
    const currentSlug = generateSlug(formData.name);
    const shouldUpdateSlug = !formData.slug || formData.slug === currentSlug;
    
    setFormData({
      ...formData,
      name: value,
      slug: shouldUpdateSlug ? generateSlug(value) : formData.slug,
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const response = await influencerService.uploadImage(file, "influencers");
      // Seguindo a lógica do NovoInfluencer.tsx
      const imagePath = response.location || response.file_name;
      setFormData({ ...formData, cover: imagePath });
      toast.success("Imagem enviada com sucesso");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Nome é obrigatório");
      return;
    }

    if (!formData.description) {
      toast.error("Descrição Completa é obrigatória");
      return;
    }

    try {
      setLoading(true);

      // Espelhando a lógica de dataToSave do NovoInfluencer.tsx
      const dataToSave = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
        meta_title: formData.name,
        meta_description: formData.description,
        summary: formData.summary || formData.description.substring(0, 150)
      };

      const response = await influencerService.createInfluencer(dataToSave);
      
      toast.success("Influencer criada com sucesso!");
      // influencerService já retorna o objeto no data ou no corpo
      onSuccess(response.data || response);
      onClose();
      
      // Reset
      setFormData({
        name: "",
        slug: "",
        description: "",
        summary: "",
        cover: "",
        active: true,
      });
    } catch (error: any) {
      console.error("Erro ao salvar influencer:", error);
      if (error.response?.status === 422) {
        const validationErrors = error.response.data.errors;
        const messages = Object.values(validationErrors).flat();
        toast.error(messages[0] as string || "Erro de validação");
      } else {
        toast.error("Erro ao criar influencer");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg relative border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4C070]/10 flex items-center justify-center text-[#D4C070]">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Nova Influencer</h3>
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Cadastro Rápido</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Foto */}
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase text-gray-400 flex items-center gap-2">
              <ImageIcon className="h-3 w-3" /> Foto da Influencer (340x280)
            </Label>
            
            {formData.cover ? (
              <div className="relative w-full h-48 rounded-xl overflow-hidden group">
                <img src={formData.cover} alt="Preview" className="w-full h-full object-cover object-top" />
                <button 
                  onClick={() => setFormData({ ...formData, cover: "" })}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#D4C070] transition-colors bg-gray-50/30">
                {uploadingImage ? (
                  <Loader2 className="h-8 w-8 text-[#D4C070] mx-auto animate-spin" />
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <Label htmlFor="modal-cover-upload" className="cursor-pointer text-sm font-medium text-[#D4C070] hover:underline">
                      Selecionar Imagem
                    </Label>
                    <input 
                      id="modal-cover-upload" 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleImageUpload} 
                    />
                  </>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-gray-400">Nome *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => handleNameChange(e.target.value)} 
                placeholder="Ex: Maria Joana" 
                className="h-10 border-gray-200" 
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-gray-400">Slug (URL)</Label>
              <Input 
                value={formData.slug} 
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })} 
                placeholder="maria-joana" 
                className="h-10 border-gray-200 text-gray-500" 
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-gray-400">Descrição Completa *</Label>
            <Textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              placeholder="Fale um pouco sobre ela..." 
              rows={3} 
              className="border-gray-200 resize-none" 
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-gray-400">Resumo (Opcional)</Label>
            <Input 
              value={formData.summary} 
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })} 
              placeholder="Breve frase..." 
              className="h-10 border-gray-200" 
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading} className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-[#D4C070] hover:bg-[#D4C070]/90 text-white px-8 font-bold shadow-lg shadow-[#D4C070]/20 min-w-[160px]" 
            disabled={loading}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "CADASTRAR INFLUENCER"}
          </Button>
        </div>
      </div>
    </div>
  );
}
